import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './CustomerLogin.css';
import { supabase } from '../config/supabase';

// Simple password hashing function (for demo - use proper hashing in production)
const hashPassword = async (password) => {
  // Simple hash for demo - in production use bcrypt or similar
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Sign up form state
  const [signUpForm, setSignUpForm] = useState({
    name: '',
    email: '',
    phone: '',
    deliveryAddress: '',
    password: '',
    confirmPassword: ''
  });

  const handleLoginChange = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSignUpChange = (e) => {
    setSignUpForm({
      ...signUpForm,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Hash the password
      const hashedPassword = await hashPassword(loginForm.password);

      // Check if customer exists with matching email and password
      const { data, error: loginError } = await supabase
        .from('customers')
        .select('*')
        .eq('email', loginForm.email.toLowerCase().trim())
        .eq('password', hashedPassword)
        .eq('is_active', true)
        .single();

      if (loginError) {
        if (loginError.code === 'PGRST116') {
          // No rows returned
          setError('Invalid email or password. Please try again.');
        } else {
          throw loginError;
        }
        setLoading(false);
        return;
      }

      if (data) {
        // Update last login
        await supabase
          .from('customers')
          .update({ last_login: new Date().toISOString() })
          .eq('id', data.id);

        // Store customer data in localStorage
        localStorage.setItem('customer', JSON.stringify({
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          delivery_address: data.delivery_address
        }));

        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Reload to update header state
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (signUpForm.password !== signUpForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (signUpForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!signUpForm.name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }

    if (!signUpForm.email.trim()) {
      setError('Please enter your email');
      setLoading(false);
      return;
    }

    if (!signUpForm.deliveryAddress.trim()) {
      setError('Please enter your delivery address');
      setLoading(false);
      return;
    }

    try {
      // Check if email already exists
      const { data: existingCustomer } = await supabase
        .from('customers')
        .select('email')
        .eq('email', signUpForm.email.toLowerCase().trim())
        .single();

      if (existingCustomer) {
        setError('An account with this email already exists. Please sign in instead.');
        setLoading(false);
        return;
      }

      // Hash the password
      const hashedPassword = await hashPassword(signUpForm.password);

      // Insert new customer into database
      const { data, error: signUpError } = await supabase
        .from('customers')
        .insert([
          {
            name: signUpForm.name.trim(),
            email: signUpForm.email.toLowerCase().trim(),
            phone: signUpForm.phone.trim() || null,
            delivery_address: signUpForm.deliveryAddress.trim(),
            password: hashedPassword,
            is_active: true
          }
        ])
        .select()
        .single();

      if (signUpError) throw signUpError;

      if (data) {
        setSuccess('Account created successfully! You can now sign in.');
        setTimeout(() => {
          setIsSignUp(false);
          setSignUpForm({
            name: '',
            email: '',
            phone: '',
            deliveryAddress: '',
            password: '',
            confirmPassword: ''
          });
          // Pre-fill email in login form
          setLoginForm({
            email: signUpForm.email,
            password: ''
          });
        }, 2000);
      }
    } catch (err) {
      if (err.code === '23505') {
        // Unique constraint violation
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="customer-login-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <div className="customer-login-main">
        <div className="customer-login-container">
          <div className="customer-login-card">
            {/* Header */}
            <div className="customer-login-header">
              <h1 className="customer-login-title">
                {isSignUp ? 'Create Account' : 'Welcome Back'}
              </h1>
              <p className="customer-login-subtitle">
                {isSignUp 
                  ? 'Sign up to start ordering delicious food' 
                  : 'Sign in to continue to your account'}
              </p>
            </div>

            {/* Toggle Buttons */}
            <div className="customer-login-toggle">
              <button
                className={`customer-login-toggle-btn ${!isSignUp ? 'customer-login-toggle-active' : ''}`}
                onClick={() => {
                  setIsSignUp(false);
                  setError('');
                  setSuccess('');
                }}
              >
                Sign In
              </button>
              <button
                className={`customer-login-toggle-btn ${isSignUp ? 'customer-login-toggle-active' : ''}`}
                onClick={() => {
                  setIsSignUp(true);
                  setError('');
                  setSuccess('');
                }}
              >
                Sign Up
              </button>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="customer-login-message customer-login-error">
                <span className="customer-login-message-icon">⚠️</span>
                <span className="customer-login-message-text">{error}</span>
              </div>
            )}

            {success && (
              <div className="customer-login-message customer-login-success">
                <span className="customer-login-message-icon">✓</span>
                <span className="customer-login-message-text">{success}</span>
              </div>
            )}

            {/* Login Form */}
            {!isSignUp && (
              <form className="customer-login-form" onSubmit={handleLogin}>
                <div className="customer-login-form-group">
                  <label className="customer-login-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    className="customer-login-input"
                    placeholder="Enter your email"
                    value={loginForm.email}
                    onChange={handleLoginChange}
                    required
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    className="customer-login-input"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={handleLoginChange}
                    required
                  />
                </div>

                <div className="customer-login-options">
                  <label className="customer-login-checkbox-label">
                    <input type="checkbox" className="customer-login-checkbox" />
                    <span>Remember me</span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="customer-login-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Sign Up Form */}
            {isSignUp && (
              <form className="customer-login-form" onSubmit={handleSignUp}>
                <div className="customer-login-form-group">
                  <label className="customer-login-label">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    className="customer-login-input"
                    placeholder="Enter your full name"
                    value={signUpForm.name}
                    onChange={handleSignUpChange}
                    required
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    className="customer-login-input"
                    placeholder="Enter your email"
                    value={signUpForm.email}
                    onChange={handleSignUpChange}
                    required
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="customer-login-input"
                    placeholder="Enter your phone number"
                    value={signUpForm.phone}
                    onChange={handleSignUpChange}
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">
                    Delivery Address <span className="customer-login-required">*</span>
                  </label>
                  <textarea
                    name="deliveryAddress"
                    className="customer-login-textarea"
                    placeholder="Enter your delivery address"
                    rows={3}
                    value={signUpForm.deliveryAddress}
                    onChange={handleSignUpChange}
                    required
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    className="customer-login-input"
                    placeholder="Create a password (min. 6 characters)"
                    value={signUpForm.password}
                    onChange={handleSignUpChange}
                    required
                    minLength={6}
                  />
                </div>

                <div className="customer-login-form-group">
                  <label className="customer-login-label">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    className="customer-login-input"
                    placeholder="Confirm your password"
                    value={signUpForm.confirmPassword}
                    onChange={handleSignUpChange}
                    required
                    minLength={6}
                  />
                </div>

                <button
                  type="submit"
                  className="customer-login-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}

            {/* Footer */}
            <div className="customer-login-footer">
              {!isSignUp ? (
                <p className="customer-login-footer-text">
                  Don't have an account?{' '}
                  <button
                    className="customer-login-footer-link"
                    onClick={() => setIsSignUp(true)}
                  >
                    Sign Up
                  </button>
                </p>
              ) : (
                <p className="customer-login-footer-text">
                  Already have an account?{' '}
                  <button
                    className="customer-login-footer-link"
                    onClick={() => setIsSignUp(false)}
                  >
                    Sign In
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;

